import React from "react";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import { Box, IconButton, Menu, MenuItem, Tooltip } from "@mui/material";
import type { RbacPolicy } from "components/authorization/RbacPolicies";
import { useRbacContext } from "components/authorization/RbacProvider";

const ITEM_HEIGHT = 48;

// Off-screen but still in the accessibility tree, so aria-describedby has something to point at.
const visuallyHidden = {
  position: "absolute",
  width: 1,
  height: 1,
  overflow: "hidden",
  clip: "rect(0 0 0 0)",
  whiteSpace: "nowrap",
} as const;

export const Overflow = {
  menuItem: (label: string, onClick: () => void): OverflowMenuItem => {
    return { label, onClick };
  },
  policyAuthorizedMenuItem: (requiredPolicy: RbacPolicy, label: string, onClick: () => void): OverflowMenuItem => {
    return { requiredPolicy, label, onClick };
  },
  // Stays in the menu without the policy, disabled and captioned, rather than vanishing — for
  // actions whose absence would read as "this row cannot be removed" instead of "not you".
  policyDisabledMenuItem: (
    requiredPolicy: RbacPolicy,
    label: string,
    onClick: () => void,
    unauthorizedTooltip: string
  ): OverflowMenuItem => {
    return { disablingPolicy: requiredPolicy, label, onClick, unauthorizedTooltip };
  },
};

export interface OverflowMenuItem {
  requiredPolicy?: RbacPolicy;
  disablingPolicy?: RbacPolicy;
  unauthorizedTooltip?: string;
  label: string;
  onClick: () => void;
}

export type OverflowMenuProps = {
  items: Array<OverflowMenuItem>;
};

export function OverflowMenu(props: OverflowMenuProps) {
  const { items } = props;
  const { isAuthorized } = useRbacContext();
  // Every row of a grid renders one of these menus with the same labels, so the ids the items are
  // described by come off a per-instance base rather than the label they show.
  const instanceId = React.useId();
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  const authorizedMenuItems = items.filter((it) => !it.requiredPolicy || isAuthorized(it.requiredPolicy));

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };

  const onMenuItemClicked = (item: OverflowMenuItem) => {
    handleClose();
    item.onClick();
  };

  if (items.length === 0) {
    return null;
  }

  return (
    <div>
      <IconButton
        aria-label="more"
        id="long-button"
        aria-controls={open ? "long-menu" : undefined}
        aria-expanded={open ? "true" : undefined}
        aria-haspopup="true"
        onClick={handleClick}
        // Counts hidden items only: a policy-disabled item is still worth opening the menu for, since
        // the whole point of keeping it is to say why it can't be used.
        disabled={!authorizedMenuItems.length}
      >
        <MoreVertIcon />
      </IconButton>
      <Menu
        id="long-menu"
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        slotProps={{
          paper: {
            style: {
              maxHeight: ITEM_HEIGHT * 5.0,
            },
          },
          list: {
            "aria-labelledby": "long-button",
          },
        }}
      >
        {authorizedMenuItems.map((item, index) => {
          const disabled = !!item.disablingPolicy && !isAuthorized(item.disablingPolicy);
          const explanation = disabled ? item.unauthorizedTooltip : undefined;
          const explanationId = explanation ? `${instanceId}-item-${index}-reason` : undefined;

          const menuItem = (
            <MenuItem disabled={disabled} aria-describedby={explanationId} onClick={() => onMenuItemClicked(item)}>
              {item.label}
            </MenuItem>
          );

          // A disabled MenuItem fires no pointer events, so the tooltip has to hang off a wrapper — which
          // leaves it invisible to a screen reader. The same text is repeated in a hidden span the item
          // describes itself by, so the reason is announced rather than just shown on hover.
          return explanation ? (
            <Tooltip key={item.label} title={explanation}>
              <span>
                {menuItem}
                <Box component="span" id={explanationId} sx={visuallyHidden}>
                  {explanation}
                </Box>
              </span>
            </Tooltip>
          ) : (
            <React.Fragment key={item.label}>{menuItem}</React.Fragment>
          );
        })}
      </Menu>
    </div>
  );
}
